<template>
  <address 
    class="footer-contacts-layout"
    :itemscope="true"
    itemtype="https://schema.org/LocalBusiness"
  >
    <div class="footer-contacts-columns">
      <div class="footer-contacts-info" aria-label="Контактные данные и адрес">
        <ul class="footer-contact-list">
          <li class="footer-contact-item">
            <span class="footer-contact-icon" aria-hidden="true">
              <PhPhone :size="18" :weight="'fill'" />
            </span>
            <div class="footer-contact-body">
              <a 
                :href="region.phoneHref" 
                class="footer-contact-link"
                :aria-label="`Позвонить по телефону: ${region.phone}`"
                itemprop="telephone"
              >
                {{ region.phone }}
              </a>
              <div class="footer-contact-caption-wrapper">
                <span v-typo class="footer-contact-caption">Бесплатный звонок по России</span>
                <span v-typo class="footer-contact-caption-secondary">Единая телефонная линия <span class="footer-contact-region">(по {{ regionNameDative }})</span></span>
              </div>
            </div>
          </li>
          <li class="footer-contact-item">
            <span class="footer-contact-icon" aria-hidden="true">
              <PhMapPin :size="18" :weight="'fill'" />
            </span>
            <div class="footer-contact-body">
              <p class="footer-address-text" itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
                <span itemprop="streetAddress">{{ region.address }}</span>
              </p>
            </div>
          </li>
          <li class="footer-contact-item">
            <span class="footer-contact-icon" aria-hidden="true">
              <PhClock :size="18" :weight="'fill'" />
            </span>
            <div 
              class="footer-contact-body footer-contact-body--hours"
              itemprop="openingHoursSpecification"
              itemscope
              itemtype="https://schema.org/OpeningHoursSpecification"
            >
              <div class="footer-hours-container">
                <div class="footer-hours-schedule">
                  <span 
                    class="footer-hours-days"
                    itemprop="dayOfWeek"
                    itemscope
                    itemtype="https://schema.org/DayOfWeek"
                  >
                    <meta itemprop="name" content="Monday,Tuesday,Wednesday,Thursday,Friday,Saturday" />
                    {{ formattedHours.days }}
                  </span>
                  <div class="flex items-baseline gap-2 flex-wrap">
                    <span class="footer-hours-time">{{ formattedHours.time }}</span>
                    <span 
                      v-if="workStatus.isOpen !== null"
                      class="footer-status-badge"
                      :class="workStatus.isOpen ? 'footer-status-badge--open' : 'footer-status-badge--closed'"
                      :aria-label="workStatus.label"
                      role="status"
                      aria-live="polite"
                    >
                      <span class="footer-status-dot" :class="workStatus.isOpen ? 'footer-status-dot--open' : 'footer-status-dot--closed'"></span>
                      {{ workStatus.text }}
                    </span>
                  </div>
                </div>
                <div class="footer-hours-status">
                  <span v-if="workStatus.nextOpen && !workStatus.isOpen" class="footer-status-next">
                    {{ workStatus.nextOpen }}
                  </span>
                </div>
              </div>
              <meta itemprop="opens" content="09:00" />
              <meta itemprop="closes" content="19:00" />
            </div>
          </li>
          <li class="footer-contact-item">
            <span class="footer-contact-icon" aria-hidden="true">
              <PhEnvelope :size="18" :weight="'fill'" />
            </span>
            <div class="footer-contact-body">
              <a 
                :href="region.emailHref" 
                class="footer-contact-link"
                :aria-label="`Написать на email: ${region.email}`"
                itemprop="email"
              >
                {{ region.email }}
              </a>
            </div>
          </li>
        </ul>
      </div>

      <div class="footer-contacts-map" aria-label="Карта">
        <div v-if="region.mapEmbedUrl" class="footer-map-frame">
          <iframe
            :src="region.mapEmbedUrl"
            title="Карта региона"
            width="100%"
            frameborder="0"
            allowfullscreen="true"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            class="footer-map-iframe"
          ></iframe>
        </div>
      </div>
    </div>
  </address>
</template>

<script>
import { PhPhone, PhEnvelope, PhMapPin, PhClock } from '@phosphor-icons/vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

export default {
  name: 'FooterAddress',
  components: {
    PhPhone,
    PhEnvelope,
    PhMapPin,
    PhClock
  },
  props: {
    region: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.phone && value.email && value.address && value.phoneHref && value.emailHref
      }
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const currentTime = ref(new Date())
    let timeInterval = null

    // Получаем текущее время в часовом поясе региона
    // Возвращает объект Date, который представляет локальное время региона
    const getRegionTime = () => {
      const timezone = props.region.timezone || 'Europe/Moscow'
      const now = new Date()
      
      // Получаем строку времени в часовом поясе региона
      const regionTimeString = now.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
      
      // Парсим строку формата "MM/DD/YYYY, HH:mm:ss"
      const [datePart, timePart] = regionTimeString.split(', ')
      const [month, day, year] = datePart.split('/')
      const [hour, minute, second] = timePart.split(':')
      
      // Создаем Date объект в локальном часовом поясе браузера,
      // но с временем региона (для корректного сравнения часов и минут)
      const regionDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
      )
      
      return regionDate
    }

    const parseWorkingHours = (hoursString) => {
      // Парсим строку типа "Понедельник – Суббота: 9:00 – 19:00"
      const match = hoursString?.match(/(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})/)
      if (match) {
        return {
          open: { hour: parseInt(match[1]), minute: parseInt(match[2]) },
          close: { hour: parseInt(match[3]), minute: parseInt(match[4]) }
        }
      }
      // Fallback для формата "Пн–Сб: 9:00–18:00"
      const fallbackMatch = hoursString?.match(/(\d{1,2}):(\d{2})[–-](\d{1,2}):(\d{2})/)
      if (fallbackMatch) {
        return {
          open: { hour: parseInt(fallbackMatch[1]), minute: parseInt(fallbackMatch[2]) },
          close: { hour: parseInt(fallbackMatch[3]), minute: parseInt(fallbackMatch[4]) }
        }
      }
      return null
    }

    const formatWorkingHours = (hoursString) => {
      if (!hoursString) return { days: 'Пн–Сб', time: '9:00–18:00' }
      
      // Парсим "Понедельник – Суббота: 9:00 – 19:00"
      const fullMatch = hoursString.match(/^(.+?):\s*(.+)$/)
      if (fullMatch) {
        return {
          days: fullMatch[1].trim(),
          time: fullMatch[2].trim()
        }
      }
      
      // Парсим "Пн–Сб: 9:00–18:00"
      const shortMatch = hoursString.match(/^(.+?):\s*(.+)$/)
      if (shortMatch) {
        return {
          days: shortMatch[1].trim(),
          time: shortMatch[2].trim()
        }
      }
      
      // Если формат не распознан, возвращаем как есть
      return { days: hoursString, time: '' }
    }

    const workStatus = computed(() => {
      const now = getRegionTime() // Используем время региона, а не локальное время браузера
      const day = now.getDay() // 0 = воскресенье, 1 = понедельник, ..., 6 = суббота
      const hours = props.region.workingHours?.weekdays || 'Пн–Сб: 9:00–18:00'
      const parsed = parseWorkingHours(hours)

      // Воскресенье - всегда закрыто
      if (day === 0) {
        const nextMonday = new Date(now)
        nextMonday.setDate(now.getDate() + (8 - day) % 7)
        nextMonday.setHours(parsed?.open.hour || 9, parsed?.open.minute || 0, 0, 0)
        
        // Вычисляем сколько часов до открытия
        const hoursUntilOpen = Math.floor((nextMonday.getTime() - now.getTime()) / (1000 * 60 * 60))
        const hoursText = hoursUntilOpen <= 12 && hoursUntilOpen > 0 
          ? ` (через ${hoursUntilOpen} ${hoursUntilOpen === 1 ? 'час' : hoursUntilOpen < 5 ? 'часа' : 'часов'})`
          : ''
        
        return {
          isOpen: false,
          text: 'Сейчас закрыто',
          label: 'Офис закрыт. Откроется в понедельник',
          nextOpen: `Откроется в понедельник в ${String(parsed?.open.hour || 9).padStart(2, '0')}:${String(parsed?.open.minute || 0).padStart(2, '0')}${hoursText}`
        }
      }

      if (!parsed) {
        return { isOpen: null, text: '', label: '', nextOpen: null }
      }

      const currentMinutes = now.getHours() * 60 + now.getMinutes()
      const openMinutes = parsed.open.hour * 60 + parsed.open.minute
      const closeMinutes = parsed.close.hour * 60 + parsed.close.minute

      const isOpen = currentMinutes >= openMinutes && currentMinutes < closeMinutes

      if (isOpen) {
        const closeTime = new Date(now)
        closeTime.setHours(parsed.close.hour, parsed.close.minute, 0, 0)
        return {
          isOpen: true,
          text: 'Открыто',
          label: `Офис открыт. Закроется в ${String(parsed.close.hour).padStart(2, '0')}:${String(parsed.close.minute).padStart(2, '0')}`,
          nextOpen: `Закроется в ${String(parsed.close.hour).padStart(2, '0')}:${String(parsed.close.minute).padStart(2, '0')}`
        }
      } else {
        // Закрыто - проверяем, когда откроется
        let nextOpen = new Date(now)
        if (currentMinutes < openMinutes) {
          // Откроется сегодня
          nextOpen.setHours(parsed.open.hour, parsed.open.minute, 0, 0)
        } else {
          // Откроется завтра
          nextOpen.setDate(now.getDate() + 1)
          nextOpen.setHours(parsed.open.hour, parsed.open.minute, 0, 0)
        }
        const weekday = nextOpen.toLocaleDateString('ru-RU', { weekday: 'long' })
        const weekdayAccusative = weekday === 'понедельник' ? 'понедельник' : 
                                 weekday === 'вторник' ? 'вторник' :
                                 weekday === 'среда' ? 'среду' :
                                 weekday === 'четверг' ? 'четверг' :
                                 weekday === 'пятница' ? 'пятницу' :
                                 weekday === 'суббота' ? 'субботу' :
                                 weekday === 'воскресенье' ? 'воскресенье' : weekday
        
        // Вычисляем сколько часов до открытия
        const hoursUntilOpen = Math.floor((nextOpen.getTime() - now.getTime()) / (1000 * 60 * 60))
        const hoursText = hoursUntilOpen <= 12 && hoursUntilOpen > 0 
          ? ` (через ${hoursUntilOpen} ${hoursUntilOpen === 1 ? 'час' : hoursUntilOpen < 5 ? 'часа' : 'часов'})`
          : ''
        
        return {
          isOpen: false,
          text: 'Сейчас закрыто',
          label: `Офис закрыт. Откроется в ${weekdayAccusative} в ${String(parsed.open.hour).padStart(2, '0')}:${String(parsed.open.minute).padStart(2, '0')}`,
          nextOpen: `Откроется в ${weekdayAccusative} в ${String(parsed.open.hour).padStart(2, '0')}:${String(parsed.open.minute).padStart(2, '0')}${hoursText}`
        }
      }
    })

    onMounted(() => {
      // Обновляем время каждую минуту
      timeInterval = setInterval(() => {
        currentTime.value = new Date() // Обновляем для пересчета getRegionTime()
      }, 60000)
    })

    onUnmounted(() => {
      if (timeInterval) {
        clearInterval(timeInterval)
      }
    })

    const formattedHours = computed(() => {
      const hours = props.region.workingHours?.weekdays || 'Пн–Сб: 9:00–18:00'
      return formatWorkingHours(hours)
    })

    const regionNameDative = computed(() => {
      const name = props.region.name || ''
      // Склонение в дательный падеж
      if (name.includes('Ставропольский край')) {
        return 'Ставропольскому краю'
      }
      if (name.includes('Москва и МО') || (name.includes('Москва') && name.includes('МО'))) {
        return 'Москве и МО'
      }
      if (name.includes('Москва')) {
        return 'Москве'
      }
      if (name.includes('Ставрополь')) {
        return 'Ставрополю'
      }
      // Если не найдено специальное правило, возвращаем как есть
      return name
    })

    return {
      workStatus,
      currentTime,
      formattedHours,
      regionNameDative
    }
  }
}
</script>

<style scoped>
address {
  font-style: normal;
}

.footer-contacts-layout {
  width: 100%;
}

.footer-contacts-columns {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

@media (min-width: 768px) {
  .footer-contacts-columns {
    flex-direction: row;
    gap: 1.25rem;
    align-items: stretch;
  }
}


.footer-contacts-info,
.footer-contacts-map {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  flex: 1;
  min-width: 0;
  align-self: stretch;
}

.footer-contact-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.footer-contact-item {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.75rem;
  align-items: start;
}

.footer-contact-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #DC2626;
}

.footer-contact-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.footer-contact-body--hours {
  gap: 0;
}

.footer-contact-link {
  font-size: 15px;
  font-weight: 500;
  color: #0f172a;
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 1.45;
}

/* Телефон - жирнее, но того же размера */
.footer-contact-item:first-child .footer-contact-link {
  font-weight: 700;
}

.footer-contact-link:hover {
  color: var(--color-brand, #DC2626);
}

.footer-contact-caption-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.footer-contact-caption {
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
}

.footer-contact-caption-secondary {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.footer-hours-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-hours-schedule {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.footer-hours-days {
  font-size: 15px;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.4;
}

.footer-hours-time {
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  line-height: 1.4;
  display: inline-block;
}

.footer-hours-status {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.footer-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  transition: all 0.2s ease;
  vertical-align: baseline;
}

.footer-status-badge--open {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.footer-status-badge--closed {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.footer-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.footer-status-dot--open {
  background: #16a34a;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
}

.footer-status-dot--closed {
  background: #dc2626;
  box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
  animation: pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
  }
  50% {
    opacity: 0.8;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0);
  }
}

@keyframes pulse-red {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
  }
  50% {
    opacity: 0.8;
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0);
  }
}

.footer-status-next {
  display: block;
  font-size: 13px;
  line-height: 1.4;
  color: #64748b;
  font-weight: 400;
}

.footer-contacts-map {
  justify-content: flex-start;
  align-self: stretch;
}

@media (min-width: 768px) {
  .footer-contacts-info {
    flex: 0 1 340px;
    max-width: 42%;
    min-width: 280px;
  }

  .footer-contacts-map {
    flex: 1 1 58%;
    min-width: 0;
  }
}

@media (min-width: 1280px) {
  .footer-contacts-columns {
    gap: 2rem;
  }

  .footer-contacts-info {
    flex: 0 1 36%;
    max-width: 36%;
  }

  .footer-contacts-map {
    flex: 1 1 64%;
  }
}

.footer-address-text {
  font-size: 15px;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.45;
  margin: 0;
}

.footer-map-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-brand, #DC2626);
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.footer-map-link:hover {
  opacity: 0.8;
}

.footer-map-frame {
  width: 100%;
  flex: 1;
  min-height: 200px;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.6) 0%, rgba(241, 245, 249, 0.8) 100%);
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.08);
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .footer-map-frame {
    min-height: 220px;
  }
}

@media (min-width: 1280px) {
  .footer-map-frame {
    min-height: 260px;
  }
}

.footer-map-iframe,
.footer-map-frame iframe {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100%;
  border: 0;
  flex: 1;
}
</style>

